import { Dispatch, DispatchWithoutAction, useCallback, useEffect, useRef } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

interface ResilientTriggeredHookBuilderProps<PrimaryHookProps, SecondaryHookProps> {
  primaryHookProps: PrimaryHookProps;
  secondaryHookProps: SecondaryHookProps;
}

/** Constructs a resilient version of the primary hook.
 * If the primary hook failure fulfills the predicate, the secondary hook will be triggered.
 * If the secondary hook response fulfills the predicate, then the primary hook will be executed again and the result
 * will be returned.
 * @param primaryHook The primary hook to be executed. If this fails, the error will be matched with the secondary hook
 * trigger predicate.
 * @param secondaryHook The secondary hook to be executed if the primary hook fails. If it returns true, the primary
 * hook will be executed again.
 * @param triggerSecondaryHookPredicate A predicate function that determines whether the secondary hook should be
 * triggered based on the primary hook's error.
 */
function ResilientTriggeredHookBuilder<
  PrimaryHookResponse = Nullable<unknown>,
  PrimaryHookTrigger = undefined,
  PrimaryHookError = boolean,
  PrimaryHookProps = undefined,
  SecondaryHookProps = undefined
>(
  primaryHook: TriggeredHook<PrimaryHookResponse, PrimaryHookTrigger, PrimaryHookError, PrimaryHookProps>,
  secondaryHook: TriggeredHook<boolean, undefined, boolean, SecondaryHookProps>,
  triggerSecondaryHookPredicate: (primaryHookError: PrimaryHookError) => boolean
): TriggeredHook<
  PrimaryHookResponse,
  PrimaryHookTrigger,
  PrimaryHookError,
  ResilientTriggeredHookBuilderProps<PrimaryHookProps, SecondaryHookProps>
> {
  return ({
    primaryHookProps,
    secondaryHookProps
  }: ResilientTriggeredHookBuilderProps<PrimaryHookProps, SecondaryHookProps>) => {
    const primaryTriggerRef = useRef<Nullable<PrimaryHookTrigger>>(null);
    const {
      result: primaryHookResult,
      error: primaryHookError,
      isLoading: primaryHookLoading,
      execute
    } = primaryHook(primaryHookProps);
    const {
      result: secondaryHookResult,
      error: secondaryHookError,
      isLoading: secondaryHookLoading,
      execute: executeSecondaryHook
    } = secondaryHook(secondaryHookProps);

    useEffect(() => {
      if (primaryHookError !== null && triggerSecondaryHookPredicate(primaryHookError)) executeSecondaryHook();
    }, [primaryHookError]);

    useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      if (secondaryHookResult) execute(primaryTriggerRef.current!);
    }, [secondaryHookResult]);

    const executePrimaryHook: Dispatch<PrimaryHookTrigger> = useCallback((props: PrimaryHookTrigger) => {
      primaryTriggerRef.current = props;
      execute(props);
    }, []);

    return {
      result: primaryHookResult,
      error:
        primaryHookError && (secondaryHookError || !triggerSecondaryHookPredicate(primaryHookError))
          ? primaryHookError
          : null,
      execute: executePrimaryHook as PrimaryHookTrigger extends undefined
        ? DispatchWithoutAction
        : Dispatch<PrimaryHookTrigger>,
      isLoading: primaryHookLoading || secondaryHookLoading
    };
  };
}

export default ResilientTriggeredHookBuilder;
